import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// =====================================================================
// Cross-domain / production safety net
// ---------------------------------------------------------------------
// In production the app may be served from a custom domain (ex:
// gestluxymove.fr) while REACT_APP_BACKEND_URL was baked at build time
// with a different host (preview URL). Cross-origin cookies are then
// dropped by most browsers on refresh.
//
// We patch both axios AND window.fetch so that every API call is
// rewritten at runtime to use the current page origin when:
//   - we run in a browser, AND
//   - the page is NOT served from the preview host
// This makes /api/... calls same-origin → cookies become first-party
// and persist normally across refresh / new tabs.
// =====================================================================
let _baseUrlInterceptorInstalled = false;
function installBaseUrlInterceptor() {
    if (_baseUrlInterceptorInstalled) return;
    if (typeof window === 'undefined' || !window.location) return;
    _baseUrlInterceptorInstalled = true;
    const pageOrigin = window.location.origin;
    const pageHost = window.location.host;

    // Is this a deployed production-like environment ? Anything that
    // isn't localhost or the Emergent PREVIEW domain is treated as prod.
    const isPreviewHost = /\.preview\.emergentagent\.com$/i.test(pageHost);
    const isLocalHost = /^localhost(:\d+)?$/i.test(pageHost) || pageHost.startsWith('127.0.0.1');
    const shouldRewrite = !isPreviewHost && !isLocalHost;
    if (!shouldRewrite) return;

    let configuredHost = '';
    try {
        configuredHost = new URL(API_URL || '').host;
    } catch {
        configuredHost = '';
    }
    // Nothing to do if the configured host is already the current page.
    if (configuredHost && configuredHost === pageHost) return;

    const rewrite = (url) => {
        if (!url || typeof url !== 'string') return url;
        if (!url.includes('/api/')) return url;
        try {
            if (/^https?:\/\//i.test(url)) {
                const parsed = new URL(url);
                // Redirect anything that points to a DIFFERENT host than
                // where we are served from (typically the old preview URL
                // baked into the JS bundle).
                if (parsed.host !== pageHost) {
                    return pageOrigin + parsed.pathname + parsed.search + parsed.hash;
                }
            }
        } catch {
            /* ignore */
        }
        return url;
    };

    // --- axios ---
    axios.interceptors.request.use((config) => {
        if (typeof config.url === 'string') {
            config.url = rewrite(config.url);
        }
        // Force credentials so our cookies travel.
        config.withCredentials = true;
        return config;
    });

    // --- native fetch ---
    if (typeof window.fetch === 'function' && !window.fetch.__patchedForApiRewrite) {
        const origFetch = window.fetch.bind(window);
        const patched = (input, init) => {
            // Wrap in try/catch — older Chrome on tablets has been observed
            // to throw inside the Request constructor when input is itself a
            // Request that already had its body read. Falling back to the
            // unmodified call is always safer than crashing the page.
            try {
                if (typeof input === 'string') {
                    const rewritten = rewrite(input);
                    if (rewritten !== input) input = rewritten;
                }
                // Note: we deliberately do NOT try to rewrite a Request
                // object — re-constructing it has subtle gotchas (cloned
                // body, signal, etc.) that can fail on some browsers.
                const nextInit = Object.assign({ credentials: 'include' }, init || {});
                return origFetch(input, nextInit);
            } catch {
                return origFetch(input, init);
            }
        };
        patched.__patchedForApiRewrite = true;
        window.fetch = patched;
    }
}
installBaseUrlInterceptor();
// Install the permission/auto-refresh interceptor at module load so it
// catches requests made BEFORE the AuthProvider has mounted (eg. during
// the very first /api/auth/me check).

// Global response interceptor: surface 403 (granular permission denied)
// once globally so individual call sites don't need to handle it.
// Also: automatically refresh the access_token when we hit a 401 "Token
// expiré" error, then retry the original request transparently. Without
// this, users were logged out after 15 minutes of activity.
//
// IMPORTANT — Random logout protection
// ------------------------------------
// We must distinguish a TRULY DEAD session (refresh_token expired or
// invalid → backend responds 401) from a TRANSIENT failure (pod restart,
// flaky wifi, browser blocking cookie momentarily, 5xx, CORS preflight
// timeout, etc.). Only the former should kick the user back to the
// login screen — otherwise tablets get logged out mid-typing for no
// good reason.
let _interceptorInstalled = false;
let _refreshPromise = null;
let _lastSessionExpiredAt = 0;
let _consecutiveRefresh401 = 0;
function _dispatchSessionExpired() {
    // Debounce so concurrent failed requests don't spam the listener.
    const now = Date.now();
    if (now - _lastSessionExpiredAt < 2000) return;
    _lastSessionExpiredAt = now;
    try { window.dispatchEvent(new CustomEvent('auth:session-expired')); } catch { /* noop */ }
}
function installPermissionInterceptor() {
    if (_interceptorInstalled) return;
    _interceptorInstalled = true;
    axios.interceptors.response.use(
        (resp) => resp,
        async (error) => {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            const originalConfig = error?.config;

            // --- Auto refresh sur 401 ---
            const isAuthCall = typeof originalConfig?.url === 'string' && (
                originalConfig.url.includes('/api/auth/refresh') ||
                originalConfig.url.includes('/api/auth/login') ||
                originalConfig.url.includes('/api/auth/logout')
            );
            if (status === 401 && originalConfig && !originalConfig._retried && !isAuthCall) {
                originalConfig._retried = true;
                try {
                    // Coalesce concurrent refreshes into a single HTTP call.
                    if (!_refreshPromise) {
                        _refreshPromise = axios.post(
                            `${API_URL}/api/auth/refresh`,
                            {},
                            { withCredentials: true },
                        ).finally(() => { _refreshPromise = null; });
                    }
                    await _refreshPromise;
                    _consecutiveRefresh401 = 0;
                    return axios(originalConfig);
                } catch (refreshErr) {
                    // Refresh failed. There are two very different cases:
                    //   1) Backend explicitly returned 401/403  → refresh_token
                    //      cookie is gone / expired → session is truly dead.
                    //   2) No response (network error, 5xx, CORS preflight
                    //      blocked, pod restart, etc.) → DO NOT log out, the
                    //      session may still be perfectly valid. Just let the
                    //      original request fail; the user can retry.
                    //
                    // Even for case (1) we require TWO consecutive failures
                    // before logging out. Some browsers (notably iPad Safari
                    // when waking from sleep) momentarily fail to attach the
                    // refresh_token cookie on the very first request, then
                    // recover on the next call. A single transient 401 should
                    // never end the session.
                    const refreshStatus = refreshErr?.response?.status;
                    if (refreshStatus === 401 || refreshStatus === 403) {
                        _consecutiveRefresh401 += 1;
                        if (_consecutiveRefresh401 >= 2) {
                            _consecutiveRefresh401 = 0;
                            _dispatchSessionExpired();
                        }
                    }
                    // For any other failure (network/5xx), stay logged in.
                }
            }

            if (status === 403 && typeof detail === 'string' && detail.startsWith('Accès refusé')) {
                toast.error(detail);
            }
            return Promise.reject(error);
        }
    );
}

function formatApiErrorDetail(detail) {
    if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Install early so requests made during the very first module imports
// (eg. usePresence heartbeat) already get the auto-refresh.
installPermissionInterceptor();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
                withCredentials: true
            });
            setUser(response.data.user);
        } catch (error) {
            // Only flip to "logged out" when the backend explicitly says so
            // (401/403). On a network error (no response) we still flip on
            // the very first boot so the app shows the login screen
            // instead of a stuck loader.
            const status = error?.response?.status;
            if (status === 401 || status === 403 || !error?.response) {
                setUser(false);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        installPermissionInterceptor();
        checkAuth();
        // When refresh_token eventually expires (after 7 days), flip the
        // user to logged-out state so the router shows the login page
        // instead of a blank screen.
        const onExpired = () => setUser(false);
        window.addEventListener('auth:session-expired', onExpired);
        return () => window.removeEventListener('auth:session-expired', onExpired);
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(
                `${API_URL}/api/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            _consecutiveRefresh401 = 0;
            setUser(response.data.user);
            return { success: true };
        } catch (error) {
            const message = formatApiErrorDetail(error.response?.data?.detail);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const refreshToken = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            return true;
        } catch (error) {
            setUser(false);
            return false;
        }
    };

    const value = {
        user,
        loading,
        login,
        logout,
        refreshToken,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

// Modules whose `ecriture` permission implicitly grants `ecriture` on the
// target key. Mirrors backend permissions.IMPLICIT_WRITE_SOURCES.
const IMPLICIT_WRITE_SOURCES = {
    prospects: ['inventaire', 'devis', 'clients', 'agenda', 'agenda_demenagement', 'carte_groupage'],
    inventaire: ['devis', 'clients'],
};

// Role-based default ecriture grants. Mirrors backend ROLE_DEFAULT_ECRITURE.
const ROLE_DEFAULT_ECRITURE = {
    assistante: ['espace_demenageurs'],
};

// useCan(moduleId)
// Returns helpers reflecting the granular permissions stored on the user.
// Rules:
//   - admin role always allows everything
//   - if the user has no permissions object → allow (legacy)
//   - otherwise read entry.lecture/ecriture/suppression (defaults to false)
//   - for `canWrite`, falls back to IMPLICIT_WRITE_SOURCES so editing an
//     inventaire/devis/client also unlocks the underlying prospect save.
export function useCan(moduleId) {
    const { user } = useAuth();
    if (!user || user === false) {
        return { canRead: false, canWrite: false, canDelete: false, canChangeStatus: false };
    }
    if (user.role === 'admin') {
        return { canRead: true, canWrite: true, canDelete: true, canChangeStatus: true };
    }
    const perms = user.permissions;
    if (!perms || typeof perms !== 'object' || Object.keys(perms).length === 0) {
        return { canRead: true, canWrite: true, canDelete: true, canChangeStatus: true };
    }
    const entry = perms[moduleId];
    const entryAllows = (mod, act) => {
        const e = perms[mod];
        return !!(e && typeof e === 'object' && e[act]);
    };
    if (!entry) {
        // Module not declared → read allowed (legacy default), write/delete denied
        // unless an implicit write source applies.
        const implicitWrite = (IMPLICIT_WRITE_SOURCES[moduleId] || []).some((src) => entryAllows(src, 'ecriture'));
        const roleWrite = (ROLE_DEFAULT_ECRITURE[user.role] || []).includes(moduleId);
        return { canRead: true, canWrite: implicitWrite || roleWrite, canDelete: false, canChangeStatus: false };
    }
    const directWrite = !!entry.ecriture;
    const implicitWrite = !directWrite && (IMPLICIT_WRITE_SOURCES[moduleId] || []).some((src) => entryAllows(src, 'ecriture'));
    const roleWrite = !directWrite && !implicitWrite && (ROLE_DEFAULT_ECRITURE[user.role] || []).includes(moduleId);
    return {
        canRead: entry.lecture !== false,
        canWrite: directWrite || implicitWrite || roleWrite,
        canDelete: !!entry.suppression,
        canChangeStatus: !!entry.change_statut,
    };
}

export default AuthContext;
